import type { TimeoutCreateForm } from '../types/timeout'
import { toTimeoutClock } from './timeoutClock'

/** What the web's Add Event form holds for a new timeout when it is sent. */
export interface TimelineTimeoutInput {
  periodId: string
  /** The team's id, as the API knows it (its uid). */
  teamId: string
  /** The clock in the form's field. */
  clock: string
  /** The moment the form was sent, as an ISO instant. */
  occurredAt: string
}

/** A create that was sent: its id, and the timeout it was sent for. */
export interface TimelineTimeoutAttempt {
  uid: string
  /** The period, team and clock the create was for. */
  signature: string
}

export interface TimelineTimeoutCreatePlan {
  /** To keep until the create is answered, for another try to compare with. */
  attempt: TimelineTimeoutAttempt
  periodId: string
  form: TimeoutCreateForm
}

/**
 * The timeout a create is for: its period, its team and its clock, the clock
 * read the way the API reads it (`05:00` and `00:05:00` are one clock). The
 * moment it was sent is not part of it.
 */
const signTimeout = ({
  periodId,
  teamId,
  clock,
}: TimelineTimeoutInput): string =>
  JSON.stringify([periodId, teamId, toTimeoutClock(clock)])

/**
 * What the web's Add Event form sends for a new timeout, and the id it goes
 * under.
 *
 * Another try at the same timeout (same period, team and clock) after a create
 * that was refused or never answered keeps the last try's id, so the API
 * records the timeout once however many of the tries reached it. Anything else
 * is a new timeout and gets a new id from `makeUid`. That matters because the
 * form stays open when its team, period or clock changes: a kept id would be
 * answered with the earlier timeout (a repeated id returns the row already
 * stored and records nothing), and the new one would never be recorded.
 *
 * The clock goes in the form the API stores it. A text that is not a clock goes
 * as it is, for the API to refuse: left out, the API would record the timeout
 * at the period's own clock instead of the one typed.
 */
export const planTimelineTimeoutCreate = (
  input: TimelineTimeoutInput,
  previousAttempt: TimelineTimeoutAttempt | null,
  makeUid: () => string
): TimelineTimeoutCreatePlan => {
  const signature = signTimeout(input)
  const uid =
    previousAttempt?.signature === signature ? previousAttempt.uid : makeUid()

  return {
    attempt: { uid, signature },
    periodId: input.periodId,
    form: {
      uid,
      team_id: input.teamId,
      period_clock_time: toTimeoutClock(input.clock) ?? input.clock,
      occurred_at: input.occurredAt,
    },
  }
}
